import { APP_CONTEXTS } from "app/model"
import { ACTIONS } from "app/store/actions"
import { IAuction } from "auction/model"
import { IAudienceAssociation, IAudienceSetFilterParams } from "audience/model"
import { IForecastResult } from "campaign/model"
import { IDspDealFormat } from "dsp-deal/model"
import { IEnvironmentAssociation, IEnvironmentsForecast, IEnvironmentsSetFilterParams } from "environments/model"
import { IFormInventory, IMapInventoryLegendData, INVENTORY_MAP_VIEW_TYPES } from "form/inventory/model"
import {
  FORM_SAVE_ERRORS,
  FORM_TABS,
  IForm,
  IFormChange,
  IFormCreatives,
  IFormData,
  IFormError,
  IFormGeoTargets,
  IFormRelations,
  IFormSetParams,
  IGeoTargetsFileImport,
  IGeoTargetsForm,
  IGeoTargetsSelection,
  SELECT_FILTER_VALUES,
  SUMMARY_TABS
} from "form/model"
import { IMediaCreativeForecastById } from "media/model"
import { IOperator, IOperatorSetFilterParams, IOperatorsForecast } from "operator/model"
import { Action } from "redux"
import { ICampaignSspDeal, ISspAssociation, ISspDeal } from "ssp/model"

export interface IFormActionSetActiveTab extends Action {
  context: APP_CONTEXTS
  activeTab: FORM_TABS
}

export interface IFormActionSetActiveSummaryTab extends Action {
  context: APP_CONTEXTS
  activeSummaryTab: SUMMARY_TABS
}

export interface IFormActionSet extends Action, IFormSetParams {
  type: ACTIONS.FORM_SET
}

export interface IFormActionContext extends Action {
  context: APP_CONTEXTS
  refreshFormikForm?: boolean
}

export interface IFormActionStopSaving extends Action {
  context: APP_CONTEXTS
  configurationChanges: IFormChange[]
  lastSavedData?: IFormData
}

export interface IFormActionSaveError extends Action {
  context: APP_CONTEXTS
  saveError: FORM_SAVE_ERRORS
}

export interface IFormActionRefreshInventory extends Action {
  context: APP_CONTEXTS
}

export interface IFormActionSetRefreshInventory extends Action {
  context: APP_CONTEXTS
  result: IForecastResult
  name?: string
}

export interface IFormActionSetRelations extends Action {
  context: APP_CONTEXTS
  relations: Partial<IFormRelations>
}

export interface IFormActionSummarySet extends Action {
  form: IForm
  inventory: IFormInventory
  geoTargetsForm: IGeoTargetsForm
}

export interface IFormActionSetGeoTargetsForm extends Action {
  context: APP_CONTEXTS
  form: Partial<IGeoTargetsForm>
}

export interface IFormActionSetNoIntersectionError extends Action {
  context: APP_CONTEXTS
  hasNoIntersectionError: boolean
}

export interface IFormActionSetGeoTargetsSelection extends Action {
  context: APP_CONTEXTS
  selection: Partial<IGeoTargetsSelection>
}

export interface IFormActionSetMapVisibility extends Action {
  isVisible: boolean
  context: APP_CONTEXTS
  viewType?: INVENTORY_MAP_VIEW_TYPES
}

export interface IFormActionClearForm extends Action {
  refreshFormikForm?: boolean
  context: APP_CONTEXTS
}

export interface IFormActionSetRefreshFormikForm extends Action {
  refreshFormikForm: boolean
  context: APP_CONTEXTS
}

export interface IFormActionSetIsLoading extends Action {
  isLoading: boolean
  context: APP_CONTEXTS
}

export interface IFormActionSetIsLoaded extends Action {
  isLoaded: boolean
  context: APP_CONTEXTS
}

export interface IFormActionSetGeoTargets extends Action {
  context: APP_CONTEXTS
  geoTargets: Partial<IFormGeoTargets>
}

export interface IFormActionSetOperators extends Action {
  context: APP_CONTEXTS
  operators: IOperator[]
  screensChanges: IFormChange[]
}

export interface IFormActionSetCampaignSspDeals extends Action {
  context: APP_CONTEXTS
  campaignSspDeals: ICampaignSspDeal[]
  screensChanges: IFormChange[]
}

export interface IFormActionSetSsps extends Action {
  context: APP_CONTEXTS
  sspAssociations: ISspAssociation[]
  screensChanges: IFormChange[]
}

export interface IFormActionSetEnvironments extends Action {
  context: APP_CONTEXTS
  environmentsAssociations: IEnvironmentAssociation[]
  screensChanges: IFormChange[]
}

export interface IFormActionSetAudience extends Action {
  context: APP_CONTEXTS
  audienceChanges: IFormChange[]
  audienceAssociations: IAudienceAssociation[]
}

export interface IFormActionSetHasError extends Action {
  context: APP_CONTEXTS
  hasError: boolean
}

export interface IFormActionSetEnvironmentsFilter extends Action {
  context: APP_CONTEXTS
  params: IEnvironmentsSetFilterParams
}

export interface IFormActionSetOperatorFilter extends Action {
  context: APP_CONTEXTS
  params: IOperatorSetFilterParams
}

export interface IFormActionSetAudienceFilter extends Action {
  context: APP_CONTEXTS
  params: IAudienceSetFilterParams
}

export interface IFormActionSetMapIsRefreshing extends Action {
  context: APP_CONTEXTS
  isRefreshing: boolean
}

export interface IFormActionSetMapInventoryLegendData extends Action {
  context: APP_CONTEXTS
  data: IMapInventoryLegendData
}

export interface IFormActionSetAuctions extends Action {
  context: APP_CONTEXTS
  auctionAssociations: IAuction[]
  hasError: boolean
}

export interface IFormActionSetGeoTargetsFileImport extends Action {
  context: APP_CONTEXTS
  fileImport: Partial<IGeoTargetsFileImport>
}

export interface IFormActionSetEnvironmentsForecast extends Action {
  context: APP_CONTEXTS
  forecast: IEnvironmentsForecast
}

export interface IFormActionSetOperatorsForecast extends Action {
  context: APP_CONTEXTS
  forecast: IOperatorsForecast
}

export interface IFormActionSetCreatives extends Action {
  context: APP_CONTEXTS
  creatives: Partial<IFormCreatives>
  creativeChange?: IFormChange
}

export interface IFormActionCloseSummary extends Action {
  isForecast: boolean
  isDspDeal: boolean
}

export interface IFormActionSetAllOperators extends Action {
  context: APP_CONTEXTS
  allOperators: IOperator[]
}

export interface IFormActionSetAllSspDeals extends Action {
  context: APP_CONTEXTS
  allSspDeals: ISspDeal[]
}

export interface IFormActionSetMediaCreativeForecast extends Action {
  context: APP_CONTEXTS
  forecast: IMediaCreativeForecastById
}

export interface IFormActionSetIsSavingCampaign extends Action {
  context: APP_CONTEXTS
  isSaving: boolean
}

export interface IFormActionSetEnvironmentsExpandedIds extends Action {
  context: APP_CONTEXTS
  expandedIds: number[]
}

export interface IFormActionSetEnvironmentsIsTreeView extends Action {
  isTreeView: boolean
}

export interface IFormSetCreativeFilterAction extends IFormActionContext {
  filter: SELECT_FILTER_VALUES
}

export interface IFormSetErrorAction extends IFormActionContext {
  error: IFormError
}

export interface IFormActionSetDspDealFormats extends IFormActionContext {
  serverSideFormats: IDspDealFormat[]
  changes: IFormChange[]
}

export const formActionCreator = {
  setActiveSummaryTab(context: APP_CONTEXTS, activeSummaryTab: SUMMARY_TABS): IFormActionSetActiveSummaryTab {
    return {
      type: ACTIONS.FORM_SET_ACTIVE_SUMMARY_TAB,
      context,
      activeSummaryTab
    }
  },
  setActiveTab(context: APP_CONTEXTS, activeTab: FORM_TABS): IFormActionSetActiveTab {
    return {
      type: ACTIONS.FORM_SET_ACTIVE_TAB,
      context,
      activeTab
    }
  },
  setIsSavingCampaign(context: APP_CONTEXTS, isSaving: boolean): IFormActionSetIsSavingCampaign {
    return {
      type: ACTIONS.FORM_SET_IS_SAVING_CAMPAIGN,
      context,
      isSaving
    }
  },
  startSavingTempCampaign(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SAVING_TEMP_CAMPAIGN,
      context
    }
  },
  stopSavingTempCampaign(
    context: APP_CONTEXTS,
    configurationChanges: IFormChange[],
    lastSavedData?: IFormData
  ): IFormActionStopSaving {
    return {
      type: ACTIONS.FORM_STOP_SAVING_TEMP_CAMPAIGN,
      context,
      configurationChanges,
      lastSavedData
    }
  },
  setSaveError(context: APP_CONTEXTS, saveError: FORM_SAVE_ERRORS): IFormActionSaveError {
    return {
      type: ACTIONS.FORM_SAVE_ERROR,
      context,
      saveError
    }
  },
  setForm(params: IFormSetParams): IFormActionSet {
    return {
      type: ACTIONS.FORM_SET,
      ...params
    }
  },
  setFormGeoTargets(context: APP_CONTEXTS, geoTargets: Partial<IFormGeoTargets>): IFormActionSetGeoTargets {
    return {
      type: ACTIONS.FORM_SET_GEO_TARGETS,
      context,
      geoTargets
    }
  },
  startRefreshingInventory(context: APP_CONTEXTS): IFormActionRefreshInventory {
    return {
      type: ACTIONS.FORM_START_REFRESHING_INVENTORY,
      context
    }
  },
  hasRefreshInventoryError(context: APP_CONTEXTS): IFormActionRefreshInventory {
    return {
      type: ACTIONS.FORM_HAS_REFRESH_INVENTORY_ERROR,
      context
    }
  },
  refreshInventory(result: IForecastResult, context: APP_CONTEXTS, name?: string): IFormActionSetRefreshInventory {
    return {
      type: ACTIONS.FORM_REFRESH_INVENTORY,
      context,
      result,
      name
    }
  },
  setMapIsRefreshing(context: APP_CONTEXTS, isRefreshing: boolean): IFormActionSetMapIsRefreshing {
    return {
      type: ACTIONS.FORM_SET_MAP_IS_REFRESHING,
      context,
      isRefreshing
    }
  },
  setRelations(context: APP_CONTEXTS, relations: Partial<IFormRelations>): IFormActionSetRelations {
    return {
      type: ACTIONS.FORM_SET_RELATIONS,
      context,
      relations
    }
  },
  setIsLoading(context: APP_CONTEXTS, isLoading: boolean): IFormActionSetIsLoading {
    return {
      type: ACTIONS.FORM_START_LOADING,
      context,
      isLoading
    }
  },
  setError(context: APP_CONTEXTS, error: IFormError): IFormSetErrorAction {
    return {
      type: ACTIONS.FORM_SET_ERROR,
      context,
      error
    }
  },
  summarySet(form: IForm, inventory: IFormInventory, geoTargetsForm: IGeoTargetsForm): IFormActionSummarySet {
    return {
      type: ACTIONS.FORM_SUMMARY_SET,
      form,
      inventory,
      geoTargetsForm
    }
  },
  summaryClose(isForecast: boolean, isDspDeal: boolean): IFormActionCloseSummary {
    return {
      type: ACTIONS.FORM_SUMMARY_CLOSE,
      isForecast,
      isDspDeal
    }
  },
  setGeoTargetsForm(context: APP_CONTEXTS, form: Partial<IGeoTargetsForm>): IFormActionSetGeoTargetsForm {
    return {
      type: ACTIONS.FORM_SET_GEO_TARGETS_FORM,
      context,
      form
    }
  },
  setGeoTargetsNoIntersectionError(
    context: APP_CONTEXTS,
    hasNoIntersectionError: boolean
  ): IFormActionSetNoIntersectionError {
    return {
      type: ACTIONS.FORM_SET_NO_INTERSECTION_ERROR,
      context,
      hasNoIntersectionError
    }
  },
  setGeoTargetsFormSelection(
    context: APP_CONTEXTS,
    selection: Partial<IGeoTargetsSelection>
  ): IFormActionSetGeoTargetsSelection {
    return {
      type: ACTIONS.FORM_SET_GEO_TARGETS_SELECTION,
      context,
      selection
    }
  },
  setInventoryMapVisibility(
    context: APP_CONTEXTS,
    isVisible: boolean,
    viewType?: INVENTORY_MAP_VIEW_TYPES
  ): IFormActionSetMapVisibility {
    return {
      type: ACTIONS.FORM_SET_MAP_VISIBILITY,
      isVisible,
      context,
      viewType
    }
  },
  setInventoryMapIsLoading(context: APP_CONTEXTS, isLoading: boolean): IFormActionSetIsLoading {
    return {
      type: ACTIONS.FORM_SET_MAP_IS_LOADING,
      isLoading,
      context
    }
  },
  setInventoryMapIsLoaded(context: APP_CONTEXTS, isLoaded: boolean): IFormActionSetIsLoaded {
    return {
      type: ACTIONS.FORM_SET_MAP_IS_LOADED,
      isLoaded,
      context
    }
  },
  setFormOperators(
    context: APP_CONTEXTS,
    screensChanges: IFormChange[],
    operators: IOperator[]
  ): IFormActionSetOperators {
    return {
      type: ACTIONS.FORM_SET_OPERATORS,
      context,
      operators,
      screensChanges
    }
  },
  startSynchronizingOperator(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_OPERATORS,
      context
    }
  },
  stopSynchronizingOperator(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_OPERATORS,
      context
    }
  },
  startSynchronizingDspDealFormats(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_FORMATS,
      context
    }
  },
  stopSynchronizingDspDealFormats(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_FORMATS,
      context
    }
  },
  startLoadingOperatorsForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_LOAD_OPERATORS_FORECAST,
      context
    }
  },
  stopLoadingOperatorsForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_LOAD_OPERATORS_FORECAST,
      context
    }
  },
  setOperatorsForecast(context: APP_CONTEXTS, forecast: IOperatorsForecast): IFormActionSetOperatorsForecast {
    return {
      type: ACTIONS.FORM_SET_OPERATORS_FORECAST,
      context,
      forecast
    }
  },
  setFormSsps(
    context: APP_CONTEXTS,
    screensChanges: IFormChange[],
    sspAssociations: ISspAssociation[]
  ): IFormActionSetSsps {
    return {
      type: ACTIONS.FORM_SET_SSPS,
      context,
      sspAssociations,
      screensChanges
    }
  },
  startSynchronizingSsp(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_SSPS,
      context
    }
  },
  stopSynchronizingSsp(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_SSPS,
      context
    }
  },
  startLoadingEnvironmentsForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_LOAD_ENVIRONMENTS_FORECAST,
      context
    }
  },
  stopLoadingEnvironmentsForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_LOAD_ENVIRONMENTS_FORECAST,
      context
    }
  },
  setEnvironmentsForecast(context: APP_CONTEXTS, forecast: IEnvironmentsForecast): IFormActionSetEnvironmentsForecast {
    return {
      type: ACTIONS.FORM_SET_ENVIRONMENTS_FORECAST,
      context,
      forecast
    }
  },
  startSynchronizingEnvironments(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_ENVIRONMENTS,
      context
    }
  },
  stopSynchronizingEnvironments(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_ENVIRONMENTS,
      context
    }
  },
  setEnvironmentsFilter(context: APP_CONTEXTS, params: IEnvironmentsSetFilterParams): IFormActionSetEnvironmentsFilter {
    return {
      type: ACTIONS.FORM_SET_ENVIRONMENTS_FILTER,
      context,
      params
    }
  },
  setEnvironmentsExpandedIds(context: APP_CONTEXTS, expandedIds: number[]): IFormActionSetEnvironmentsExpandedIds {
    return {
      type: ACTIONS.FORM_SET_ENVIRONMENTS_EXPANDED_IDS,
      context,
      expandedIds
    }
  },
  setEnvironmentIsTreeView(isTreeView: boolean): IFormActionSetEnvironmentsIsTreeView {
    return {
      type: ACTIONS.FORM_SET_ENVIRONMENTS_IS_TREE_VIEW,
      isTreeView
    }
  },
  setFormEnvironments(
    context: APP_CONTEXTS,
    screensChanges: IFormChange[],
    environmentsAssociations: IEnvironmentAssociation[]
  ): IFormActionSetEnvironments {
    return {
      type: ACTIONS.FORM_SET_ENVIRONMENTS,
      context,
      environmentsAssociations,
      screensChanges
    }
  },
  startSynchronizingAudience(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_AUDIENCE,
      context
    }
  },
  stopSynchronizingAudience(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_AUDIENCE,
      context
    }
  },
  setAudiencesFilter(context: APP_CONTEXTS, params: IAudienceSetFilterParams): IFormActionSetAudienceFilter {
    return {
      type: ACTIONS.FORM_SET_AUDIENCE_FILTER,
      context,
      params
    }
  },
  setFormAudience(
    context: APP_CONTEXTS,
    audienceChanges: IFormChange[],
    audienceAssociations: IAudienceAssociation[]
  ): IFormActionSetAudience {
    return {
      type: ACTIONS.FORM_SET_AUDIENCE,
      context,
      audienceChanges,
      audienceAssociations
    }
  },
  setMapInventoryLegendData(
    context: APP_CONTEXTS,
    data: IMapInventoryLegendData
  ): IFormActionSetMapInventoryLegendData {
    return {
      type: ACTIONS.FORM_SET_MAP_LEGEND_DATA,
      context,
      data
    }
  },
  clearAppForm(context: APP_CONTEXTS, refreshFormikForm?: boolean): IFormActionClearForm {
    return {
      type: ACTIONS.FORM_CLEAR_APP_FORM,
      context,
      refreshFormikForm
    }
  },
  setRefreshFormikForm(context: APP_CONTEXTS, refreshFormikForm: boolean): IFormActionSetRefreshFormikForm {
    return {
      type: ACTIONS.FORM_REFRESH_FORMIK_FORM,
      context,
      refreshFormikForm
    }
  },
  setFormAuctions(context: APP_CONTEXTS, auctionAssociations: IAuction[], hasError: boolean): IFormActionSetAuctions {
    return {
      type: ACTIONS.FORM_SET_AUCTIONS,
      context,
      auctionAssociations,
      hasError
    }
  },
  setGeoTargetsFileImport(
    context: APP_CONTEXTS,
    coordinateImportTemp: Partial<IGeoTargetsFileImport>
  ): IFormActionSetGeoTargetsFileImport {
    return {
      type: ACTIONS.FORM_SET_GEO_TARGETS_FILE_IMPORT,
      context,
      fileImport: coordinateImportTemp
    }
  },
  setCreatives(
    context: APP_CONTEXTS,
    creatives: Partial<IFormCreatives>,
    creativeChange?: IFormChange
  ): IFormActionSetCreatives {
    return {
      type: ACTIONS.FORM_SET_CREATIVES,
      context,
      creatives,
      creativeChange
    }
  },
  setAllOperators(context: APP_CONTEXTS, allOperators: IOperator[]): IFormActionSetAllOperators {
    return {
      type: ACTIONS.FORM_SET_ALL_OPERATORS,
      context,
      allOperators
    }
  },
  setHasLoadingAllOpertorsError(context: APP_CONTEXTS, hasError: boolean): IFormActionSetHasError {
    return {
      type: ACTIONS.FORM_SET_HAS_LOADING_ALL_OPERATORS_ERROR,
      context,
      hasError
    }
  },
  startLoadingAllOperators(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_SET_IS_LOADING_ALL_OPERATORS,
      context
    }
  },
  setOperatorFilter(context: APP_CONTEXTS, params: IOperatorSetFilterParams): IFormActionSetOperatorFilter {
    return {
      type: ACTIONS.FORM_SET_OPERATOR_FILTER,
      context,
      params
    }
  },
  startLoadingMediaCreativeForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_LOAD_MEDIA_CREATIVE_FORECAST,
      context
    }
  },
  stopLoadingMediaCreativeForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_LOAD_MEDIA_CREATIVE_FORECAST,
      context
    }
  },
  setMediaCreativeForecast(
    context: APP_CONTEXTS,
    forecast: IMediaCreativeForecastById
  ): IFormActionSetMediaCreativeForecast {
    return {
      type: ACTIONS.FORM_SET_MEDIA_CREATIVE_FORECAST,
      context,
      forecast
    }
  },
  startSynchronizingMediaCreativeForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_MEDIA_CREATIVE_FORECAST,
      context
    }
  },
  stopSynchronizingMediaCreativeForecast(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_MEDIA_CREATIVE_FORECAST,
      context
    }
  },
  setHasSynchronizingMediaCreativeForecastError(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_SET_HAS_SYNCH_MEDIA_CREATIVE_FORECAST_ERROR,
      context
    }
  },
  setCreativeFilter(context: APP_CONTEXTS, filter: SELECT_FILTER_VALUES): IFormSetCreativeFilterAction {
    return {
      type: ACTIONS.FORM_SET_CREATIVE_FILTER,
      context,
      filter
    }
  },
  setAllSspDeals(context: APP_CONTEXTS, allDeals: ISspDeal[]): IFormActionSetAllSspDeals {
    return {
      type: ACTIONS.FORM_SET_ALL_SSP_DEALS,
      context,
      allSspDeals: allDeals
    }
  },
  setHasLoadingAllSspDealsError(context: APP_CONTEXTS, hasError: boolean): IFormActionSetHasError {
    return {
      type: ACTIONS.FORM_SET_HAS_LOADING_ALL_SSP_DEALS_ERROR,
      context,
      hasError
    }
  },
  startLoadingAllSspDeals(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_SET_IS_LOADING_ALL_SSP_DEALS,
      context
    }
  },
  setCampaignSspDeals(
    context: APP_CONTEXTS,
    screensChanges: IFormChange[],
    campaignSspDeals: ICampaignSspDeal[]
  ): IFormActionSetCampaignSspDeals {
    return {
      type: ACTIONS.FORM_SET_CAMPAIGN_SSP_DEALS,
      context,
      campaignSspDeals,
      screensChanges
    }
  },
  startSynchronizingSspDeals(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_START_SYNCH_SSP_DEALS,
      context
    }
  },
  stopSynchronizingSspDeal(context: APP_CONTEXTS): IFormActionContext {
    return {
      type: ACTIONS.FORM_STOP_SYNCH_SSP_DEALS,
      context
    }
  },
  setDspDealFormat(
    context: APP_CONTEXTS,
    serverSideFormats: IDspDealFormat[],
    changes: IFormChange[]
  ): IFormActionSetDspDealFormats {
    return {
      type: ACTIONS.FORM_SET_DSP_DEALS_FORMATS,
      context,
      serverSideFormats,
      changes
    }
  }
}
